// ─── NativeWidgets — shared rendering for the v2 native pipeline IR ────────────────
//
// The single source of truth for turning `native_frame` widgets + `native_commit` styled
// lines into React. Shared by WhimerTerm² (WhimerNativeView, committed = terminal lines)
// and WhimerClean² (WhimerCleanView, committed = JSONL turns) so the live zone — spinner /
// prompt card / status / menu / table / diff / raw — looks and behaves identically in both.
//
// Exports:
//   • renderLine / diffKind / renderDiffLine — styled committed-line rendering (terminal skin)
//   • groupNativeWidgets — split ir.widgets into {aboveFlow, abovePinned, below, overlay}
//   • renderNativeWidget — one widget → node (dispatches status bar vs the widget view)

import type {
  DetectedPrompt, PromptOption,
  NativeIR, NativeLine, NativeSegment, NativeStyle, NativeWidget,
} from '../../api/client'
import { InteractivePromptCard } from '../InteractivePromptCard'

// ─── Segment rendering (shared by committed + raw live widgets) ────────────────────

// ANSI color token → CSS. WhimerVT emits colors as TOKENS (ansi-0..15 / ansi256-n /
// #rrggbb), NOT css values. Without this map the browser drops 'ansi-1' and text falls
// back to the default colour — the "only white/grey, real colours missing" bug.
const ANSI16 = [
  '#000000', '#cd0000', '#00cd00', '#cdcd00', '#2222e0', '#cd00cd', '#00cdcd', '#e5e5e5',
  '#7f7f7f', '#ff5555', '#55ff55', '#ffff55', '#5c7cff', '#ff55ff', '#55ffff', '#ffffff',
]
function ansi256(n: number): string {
  if (n < 16) return ANSI16[n] ?? '#ffffff'
  if (n < 232) {
    const c = n - 16
    const cube = (v: number) => (v ? 55 + v * 40 : 0)
    return `rgb(${cube(Math.floor(c / 36))},${cube(Math.floor((c / 6) % 6))},${cube(c % 6)})`
  }
  const v = 8 + (n - 232) * 10
  return `rgb(${v},${v},${v})`
}
export function ansiColor(tok?: string): string | undefined {
  if (!tok) return undefined
  if (tok[0] === '#') return tok                                 // truecolor
  if (tok.startsWith('ansi256-')) return ansi256(Number(tok.slice(8)))
  if (tok.startsWith('ansi-')) return ANSI16[Number(tok.slice(5))] ?? tok
  return tok                                                     // already a css colour name
}

// Build the inline CSS for one styled segment. `dimDefault` (live zone, R5) fades text
// that has NO explicit foreground color so colored runs pop and plain text recedes;
// committed passes dimDefault=false so everything renders bright + true.
function segStyle(style: NativeStyle, dimDefault: boolean): React.CSSProperties {
  const css: React.CSSProperties = {}
  const fg = ansiColor(style.fg)
  const bg = ansiColor(style.bg)
  if (fg) css.color = fg
  else if (dimDefault) css.color = 'var(--text-muted)'
  if (bg) css.background = bg
  if (style.bold) css.fontWeight = 700
  if (style.italic) css.fontStyle = 'italic'
  if (style.underline) css.textDecoration = 'underline'
  if (style.dim) css.opacity = 0.6
  if (style.inverse) {
    // swap fg/bg — fall back to surface tokens when a side is unset
    css.color = bg ?? 'var(--bg)'
    css.background = fg ?? 'var(--text)'
  }
  return css
}

// Render one logical line as styled <span> runs, preserving column gaps with spaces so
// the monospace layout (R14/R21) survives. `col` is 0-based; we pad from the prior run.
export function renderLine(line: NativeLine, dimDefault: boolean): React.ReactNode {
  let cursor = 0
  const out: React.ReactNode[] = []
  line.segments.forEach((seg: NativeSegment, i) => {
    if (seg.col > cursor) out.push(' '.repeat(seg.col - cursor))
    out.push(<span key={i} style={segStyle(seg.style, dimDefault)}>{seg.text}</span>)
    cursor = seg.col + seg.text.length
  })
  return out.length ? out : ' ' // keep blank lines occupying a row
}

// ─── Diff-line styling ─────────────────────────────────────────────────────────────
// Claude renders a code edit as terminal cells with red/green BACKGROUNDS (e.g. " 1 -foo")
// where the tint only spans the painted text and the +/- is glued to the line number. We
// detect such a line and re-render it as a full-width diff row with a separate number + sign
// column (reusing the .diff-* look from the clean view) so it reads the same in both views.

function ansi256Rgb(n: number): [number, number, number] {
  if (n < 16) {
    const h = ANSI16[n] ?? '#ffffff'
    return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]
  }
  if (n < 232) {
    const c = n - 16
    const cube = (v: number) => (v ? 55 + v * 40 : 0)
    return [cube(Math.floor(c / 36)), cube(Math.floor((c / 6) % 6)), cube(c % 6)]
  }
  const v = 8 + (n - 232) * 10
  return [v, v, v]
}
function ansiRgb(tok?: string): [number, number, number] | null {
  if (!tok) return null
  if (tok[0] === '#') return [parseInt(tok.slice(1, 3), 16), parseInt(tok.slice(3, 5), 16), parseInt(tok.slice(5, 7), 16)]
  if (tok.startsWith('ansi256-')) return ansi256Rgb(Number(tok.slice(8)))
  if (tok.startsWith('ansi-')) return ansi256Rgb(Number(tok.slice(5)))
  return null
}

const DIFF_LINE_RE = /^\s*(\d*)\s*([+-])(.*)$/
// Context row inside a diff block: "      389  <code>" — number, the 1-space separator,
// then the BLANK sign slot + code (so the code group starts at the same column as an
// add/del row's). The mandatory 2 spaces keep prose like "3 files changed" from matching.
const DIFF_CTX_RE = /^\s*(\d+)(?: {2}(.*))?$/

export type DiffRowKind = 'add' | 'del' | 'ctx'

// 'add' (greenish bg) / 'del' (reddish bg) / null. Guarded by the leading "<num> <+/->"
// shape so a non-diff line that merely has a coloured background is never mistaken for a diff.
export function diffKind(line: NativeLine): 'add' | 'del' | null {
  const text = line.segments.map(s => s.text).join('')
  if (!DIFF_LINE_RE.test(text)) return null
  for (const s of line.segments) {
    const rgb = ansiRgb(s.style?.bg)
    if (!rgb) continue
    const [r, g, b] = rgb
    if (g > r + 24 && g >= b) return 'add'
    if (r > g + 24 && r >= b) return 'del'
  }
  return null
}

// Walk outward from every add/del anchor and upgrade adjacent context-shaped rows to 'ctx'
// so the whole diff block shares the gutter/sign/code columns. A numbered row far from any
// +/- (e.g. `cat -n` output, a list) never touches an anchor and is left untouched.
function expandCtx(kinds: (DiffRowKind | null)[], texts: (string | null)[]): void {
  for (let i = 0; i < kinds.length; i++) {
    if (kinds[i] !== 'add' && kinds[i] !== 'del') continue
    for (let j = i - 1; j >= 0 && kinds[j] === null && texts[j] !== null && DIFF_CTX_RE.test(texts[j]!); j--) kinds[j] = 'ctx'
    for (let j = i + 1; j < kinds.length && kinds[j] === null && texts[j] !== null && DIFF_CTX_RE.test(texts[j]!); j++) kinds[j] = 'ctx'
  }
}

// Per-line diff kinds for a contiguous block of committed/pending lines. add/del come from
// diffKind (bg tint), context neighbours are upgraded via expandCtx so every row of one
// diff renders with the same column layout (code never shifts between +/- and plain rows).
export function diffLineKinds(lines: NativeLine[]): (DiffRowKind | null)[] {
  const texts = lines.map(l => l.segments.map(s => s.text).join(''))
  const kinds: (DiffRowKind | null)[] = lines.map(l => diffKind(l))
  expandCtx(kinds, texts)
  return kinds
}

// Same per-row kinds over a WIDGET list (live zone): only widgets carrying a raw line have
// text; any other widget breaks the context run, so nothing outside the diff is upgraded.
export function widgetDiffKinds(widgets: NativeWidget[]): (DiffRowKind | null)[] {
  const texts: (string | null)[] = []
  const kinds: (DiffRowKind | null)[] = widgets.map(w => {
    const line = (w.data as { line?: NativeLine } | undefined)?.line
    if (!line?.segments) { texts.push(null); return null }
    texts.push(line.segments.map(s => s.text).join(''))
    return diffKind(line)
  })
  expandCtx(kinds, texts)
  return kinds
}

export function renderDiffLine(line: NativeLine, kind: DiffRowKind): React.ReactNode {
  const text = line.segments.map(s => s.text).join('')
  let num = ''
  let sign = ' '
  let code = ''
  if (kind === 'ctx') {
    const m = text.match(DIFF_CTX_RE)
    num = m?.[1] ?? ''
    code = m?.[2] ?? ''
  } else {
    const m = text.match(DIFF_LINE_RE)
    num = m?.[1] ?? ''
    sign = m?.[2] ?? (kind === 'add' ? '+' : '-')
    code = m?.[3] ?? text
  }
  return (
    <>
      <span className="diff-gutter">{num}</span>
      <span className="diff-sign">{sign}</span>
      <span className="diff-text">{code || ' '}</span>
    </>
  )
}

// ─── kind-specific data shapes (mirror the backend recognizer payloads) ───────────
// Backend: native/plugins/recognizers/{spinner,prompt,status,_zones}/index.ts + _coordinator.
interface SpinnerData { text?: string; label?: string; hint?: string }
interface PromptData { kind?: string; style: 'numbered' | 'yesno' | 'arrow'; question: string; options: PromptOption[]; id: string; raw?: string[]; multi?: boolean; selIndex?: number; submitSend?: string }
interface StatusData { kind?: string; fields: { value: string; align?: 'left' | 'center' | 'right'; col?: number }[] }
interface MenuData { title?: string; items: { text: string; send: string }[] }
interface TableData { rows: string[][]; header?: string[] }
interface ToolCallData { kind?: string; name: string; args: string; result?: string[] }
interface RawData { row?: number; line: NativeLine }

export interface NativeWidgetHandlers {
  onAnswer: (opt: PromptOption, text?: string) => void
  onRunMenu: (send: string) => void
  /** dismiss (close) the prompt card — persisted per chat (promptHide). */
  onDismiss?: () => void
  /** collapse the prompt card into a restore pill — persisted per chat (promptHide). */
  onHide?: () => void
}

// ─── A single native widget → React node ──────────────────────────────────────────

function NativeWidgetView({ wg, keyId, diffOverride, onAnswer, onRunMenu, onDismiss, onHide }: {
  wg: NativeWidget
  keyId: string
  /** per-row diff kind from widgetDiffKinds — lets a context row (no +/-) share the diff columns. */
  diffOverride?: DiffRowKind | null
} & NativeWidgetHandlers) {
  switch (wg.kind) {
    case 'spinner':
      // ลบตัวแสดง "กำลังพิมพ์" (spinner widget) ออก ตามคำขอ
      return null
    case 'prompt': {
      const d = wg.data as PromptData
      // Adapt the native PromptWidgetData → DetectedPrompt (InteractivePromptCard's shape).
      const prompt: DetectedPrompt = {
        id: d.id,
        question: d.question,
        options: d.options,
        kind: d.style === 'yesno' ? 'yesno' : d.style === 'arrow' ? 'arrow' : 'numbered',
        raw: d.raw,
        multi: d.multi,
        selIndex: d.selIndex,
        submitSend: d.submitSend,
      }
      return <InteractivePromptCard key={keyId} prompt={prompt} onAnswer={onAnswer} onDismiss={onDismiss} onHide={onHide} />
    }
    case 'menu': {
      const d = (wg.data ?? { items: [] }) as MenuData
      return (
        <div className="whimer-menu" key={keyId} role="group" aria-label="คำสั่ง">
          {d.title && <div className="whimer-menu-title">{d.title}</div>}
          {d.items.map((it, j) => (
            <button className="whimer-menu-item" key={j} type="button" onClick={() => onRunMenu(it.send)}>
              {it.text}
            </button>
          ))}
        </div>
      )
    }
    case 'table': {
      const d = (wg.data ?? { rows: [] }) as TableData
      return (
        <div className="whimer-native-table" key={keyId}>
          <table>
            {d.header && (
              <thead><tr>{d.header.map((h, j) => <th key={j}>{h}</th>)}</tr></thead>
            )}
            <tbody>
              {d.rows.map((row, ri) => (
                <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
    case 'tool-call': {
      const d = (wg.data ?? { name: '', args: '' }) as ToolCallData
      return (
        <div className="whimer-toolcall" key={keyId}>
          <div className="whimer-toolcall-head">
            <span className="whimer-toolcall-dot">●</span>
            <span className="whimer-toolcall-name">{d.name}</span>
            {d.args ? <span className="whimer-toolcall-args">({d.args})</span> : null}
          </div>
          {d.result && d.result.length > 0 && (
            <div className="whimer-toolcall-result">
              {d.result.map((r, j) => <div className="whimer-toolcall-result-line" key={j}>{r}</div>)}
            </div>
          )}
        </div>
      )
    }
    case 'progress':
    case 'corner':
    case 'raw':
    default: {
      // raw + unknown kinds fall back to a styled monospace line (R3 — correctness never
      // breaks). The coordinator's raw payload is { row, line: { segments } }; anything
      // else with no line just renders nothing.
      const d = wg.data as RawData | undefined
      if (d?.line) {
        // diffOverride (from widgetDiffKinds) knows about neighbour rows, so it can mark
        // 'ctx' too; without it fall back to the per-line add/del detection.
        const dk = diffOverride !== undefined ? diffOverride : diffKind(d.line)
        if (dk) {
          return (
            <pre className={`whimer-native-raw wnative-diff wnative-diff-${dk}`} key={keyId}>{renderDiffLine(d.line, dk)}</pre>
          )
        }
        // R5 + two-zone: a COMMITTED raw row (above the divider) is settled → render BRIGHT, same
        // as the native_commit log. Only LIVE rows (below the divider) dim default/white text.
        // (Before: always dimmed → committed rows above the line still looked gray = the old
        // colour system bleeding into the new position-based one.)
        return (
          <pre className="whimer-native-raw" key={keyId}>{renderLine(d.line, /*dimDefault*/ !wg.committed)}</pre>
        )
      }
      return null
    }
  }
}

// A status widget is special: its chips carry an `align` hint and we lay them out in a
// left / center / right row so a wide screen spreads them to the corners (R14) while a
// narrow screen lets flex wrap them top-to-bottom.
function NativeStatusBar({ wg, keyId }: { wg: NativeWidget; keyId: string }) {
  const d = (wg.data ?? { fields: [] }) as StatusData
  const left  = d.fields.filter(f => (f.align ?? wg.align ?? 'left') === 'left')
  const mid   = d.fields.filter(f => (f.align ?? wg.align) === 'center')
  const right = d.fields.filter(f => (f.align ?? wg.align) === 'right')
  const Chip = (f: { value: string }, j: number) => <span className="whimer-chip" key={j}>{f.value}</span>
  return (
    <div className="whimer-status whimer-status--positioned" key={keyId}>
      <div className="whimer-status-group whimer-status-group--left">{left.map(Chip)}</div>
      <div className="whimer-status-group whimer-status-group--center">{mid.map(Chip)}</div>
      <div className="whimer-status-group whimer-status-group--right">{right.map(Chip)}</div>
    </div>
  )
}

// ─── public render + grouping helpers ──────────────────────────────────────────────

// One widget → node. `status` is emitted by the backend (cast to Widget['kind']) but isn't
// a literal in the declared union, so compare as a string to dispatch it to the status bar.
export function renderNativeWidget(wg: NativeWidget, keyId: string, h: NativeWidgetHandlers, diffOverride?: DiffRowKind | null): React.ReactNode {
  return (wg.kind as string) === 'status'
    ? <NativeStatusBar wg={wg} keyId={keyId} key={keyId} />
    : <NativeWidgetView wg={wg} keyId={keyId} key={keyId} diffOverride={diffOverride}
        onAnswer={h.onAnswer} onRunMenu={h.onRunMenu}
        onDismiss={h.onDismiss} onHide={h.onHide} />
}

// JSONL-oracle dedup (spec/JSONL_ORACLE.md): the GIST matching that decides which live prose
// is already committed runs in the BACKEND now (ptyManager.tagCommittedFrame) — single source
// for all surfaces. The backend sets widget.committed; views just honor that flag.

export interface GroupedNativeWidgets {
  /** informational live widgets (raw / spinner / status) — render in the scroll flow. */
  aboveFlow: NativeWidget[]
  /** interactive widgets (prompt / menu) — PIN above the composer so they stay tappable. */
  abovePinned: NativeWidget[]
  below: NativeWidget[]
  overlay: NativeWidget[]
}

// Group the flat widget list by zone, preserving the coordinator's top→bottom order, and
// split the above-input zone into interactive (pinned) vs informational (flow). Interactive
// widgets must be ALWAYS reachable — pinning keeps a prompt card visible + tappable instead
// of buried at the end of a long scroll (esp. on mobile).
export function groupNativeWidgets(ir: NativeIR): GroupedNativeWidgets {
  const widgets = [...ir.widgets].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const above   = widgets.filter(w => (w.zone ?? 'above-input') === 'above-input')
  const below   = widgets.filter(w => w.zone === 'below-input')
  const overlay = widgets.filter(w => w.zone === 'overlay')
  const isInteractive = (w: NativeWidget) => w.kind === 'prompt' || w.kind === 'menu'
  return {
    aboveFlow: above.filter(w => !isInteractive(w)),
    abovePinned: above.filter(isInteractive),
    below,
    overlay,
  }
}
